// Invitation acceptance (B6): inspect the link, prove or create the account (prepare), then verify
// two-step and join (accept). Each is a separate server step. The link token, the challenge and any
// enrollment seed live only in this object and are cleared on cancel, account change, expiry and
// unmount. Joining always ends in a fresh ordinary sign-in.
import type { DataEnvelope } from '../lib/api.ts';
import { type InviteInspect, type PreparedMfa, DISPLAY_NAME_MAX, OTP, PASSWORD_MAX, PASSWORD_MIN, isDesktopContinuation, parseAccepted, parseInspect, parsePrepared } from './contract.ts';
import { type FlowDiagnostic, type FlowOptions, type FragmentSource, Flow, classifyFailure, diagnosticOf, loadIdentityCapabilities } from './flow.ts';
import { isAbortError } from '../lib/api.ts';

export type InvitePhase =
  | 'idle'
  | 'no_link' // no token in this page load, or a malformed one
  | 'loading'
  | 'unavailable' // identity workflows aren't on this server (404 / 503)
  | 'invalid' // expired, revoked, already used or unknown
  | 'wrong_account' // signed in as someone other than the invited address
  | 'already_member'
  | 'details' // show the invitation; ask for the password (and a name for a new account)
  | 'preparing'
  | 'verify' // two-step: enroll with the seed, enter an existing code, or just confirm
  | 'accepting'
  | 'joined'
  | 'unknown' // joining may or may not have completed
  | 'account_changed' // the account in this browser changed; the link was cleared
  | 'cancelled'
  | 'error';

export type InviteNotice = 'step_expired' | 'session_changed' | 'too_many_codes' | null;

export interface InviteSnapshot {
  phase: InvitePhase;
  invite: InviteInspect | null;
  /** Present only while verifying. `secret` only for an intentional enrollment. */
  mfa: PreparedMfa | null;
  stepExpiresAt: string | null;
  notice: InviteNotice;
  fieldError: string | null;
  attempts: number;
  /**
   * `verified`: joining used an authenticator code, which the server won't accept again, so the
   * next sign-in needs the app's next code. `existingAccount`: the account may already belong to
   * other workspaces, so sign-in can open one of those first (nothing here selects a workspace).
   */
  joined: { tenant: string; next: string; fromDesktop: boolean; verified: boolean; existingAccount: boolean } | null;
  diagnostic: FlowDiagnostic | null;
}

export interface PrepareInput {
  password: string;
  /** A name for a new account; null when proving an existing account. */
  displayName: string | null;
}

const MAX_CODE_ATTEMPTS = 5;
const STEP_SLACK_MS = 2_000;

const INITIAL: InviteSnapshot = { phase: 'idle', invite: null, mfa: null, stepExpiresAt: null, notice: null, fieldError: null, attempts: 0, joined: null, diagnostic: null };

const TERMINAL = new Set<InvitePhase>(['no_link', 'unavailable', 'invalid', 'joined', 'unknown', 'account_changed', 'cancelled']);

export class InviteFlow extends Flow<InviteSnapshot> {
  private token: string | null = null;
  private challenge: string | null = null;
  private taken = false;
  /** The challenge was prepared by proving an existing account's password. */
  private existingAccount = false;
  private stepTimer: unknown = null;
  private readonly fragments: FragmentSource;
  private readonly onSessionProblem: () => void;

  constructor(options: FlowOptions & { fragments: FragmentSource; onSessionProblem?: () => void }) {
    super(options, INITIAL);
    this.fragments = options.fragments;
    this.onSessionProblem = options.onSessionProblem ?? (() => undefined);
  }

  /** A new invitation link opened in this tab while the page is showing. */
  restart(token: string | null): void {
    this.cancel();
    this.clearSecrets();
    this.token = token;
    this.set({ ...INITIAL });
    this.load();
  }

  retry(): void {
    if (this.getSnapshot().phase === 'error') this.load();
  }

  /** Leave the flow: nothing is joined and the link is forgotten. */
  discard(): void {
    if (this.getSnapshot().phase === 'accepting') return;
    this.cancel();
    this.clearSecrets();
    this.set({ ...INITIAL, phase: 'cancelled' });
  }

  /** Back from the two-step view: the challenge and any seed are dropped. */
  back(): void {
    const phase = this.getSnapshot().phase;
    if (phase !== 'verify' && phase !== 'preparing') return;
    this.cancel();
    this.dropChallenge();
    this.set({ phase: 'details', notice: null, fieldError: null, attempts: 0 });
  }

  wipe(): void {
    this.cancel();
    this.clearSecrets();
    if (!TERMINAL.has(this.getSnapshot().phase)) this.set({ ...INITIAL, phase: 'cancelled' });
  }

  protected onAttach(): void {
    if (!this.taken) {
      this.taken = true;
      const held = this.fragments.take('invite');
      this.token = held?.token ?? null;
    }
    const phase = this.getSnapshot().phase;
    if (phase === 'idle' || (phase === 'loading' && !this.busy)) this.load();
    else if ((phase === 'preparing' || phase === 'accepting') && !this.busy) this.set({ phase: phase === 'preparing' ? 'details' : 'verify' });
  }

  protected onSessionReady(): void {
    if (this.getSnapshot().phase === 'loading' && !this.busy) this.load();
  }

  protected onAccountChanged(): void {
    this.dropStepTimer();
    if (TERMINAL.has(this.getSnapshot().phase)) return;
    this.clearSecrets();
    this.set({ ...INITIAL, phase: 'account_changed' });
  }

  protected onSessionRotated(): void {
    const phase = this.getSnapshot().phase;
    if (phase === 'loading') this.load();
    else if (phase === 'verify' || phase === 'preparing') {
      // The challenge belongs to the previous browser session.
      this.cancel();
      this.dropChallenge();
      this.set({ phase: 'details', notice: 'session_changed', fieldError: null, attempts: 0 });
    }
  }

  private load(): void {
    if (!this.token) {
      this.set({ ...INITIAL, phase: 'no_link' });
      return;
    }
    this.set({ phase: 'loading', fieldError: null, diagnostic: null });
    if (!this.sessionReady || !this.attached) return;
    void this.inspect(this.token);
  }

  private async inspect(token: string): Promise<void> {
    const { gen, signal } = this.begin();
    try {
      const caps = await loadIdentityCapabilities(this.api, signal);
      if (!this.current(gen)) return;
      if (caps.kind === 'missing') {
        this.landed(gen);
        this.set({ phase: 'unavailable', diagnostic: caps.diagnostic });
        return;
      }
      if (caps.kind === 'error') {
        this.landed(gen);
        this.set({ phase: 'error', diagnostic: caps.diagnostic });
        return;
      }
      const env = await this.api.request<DataEnvelope<unknown>>('/identity/invitations/inspect', { method: 'POST', body: JSON.stringify({ token }), signal });
      if (!this.landed(gen)) return;
      const invite = parseInspect(env?.data);
      if (!invite) {
        this.set({ phase: 'error', diagnostic: { code: 'invalid_response', requestId: null } });
        return;
      }
      if (Date.parse(invite.expires_at) <= this.now()) {
        this.clearSecrets();
        this.set({ phase: 'invalid', diagnostic: null });
        return;
      }
      // The link already arrived, so missing invitation email on the server doesn't block using it.
      this.set({ phase: 'details', invite, diagnostic: null });
    } catch (error) {
      if (isAbortError(error) || !this.landed(gen)) return;
      this.fail(error, 'inspect');
    }
  }

  /** Step 2: prove the existing account's password, or create the account. */
  async prepare(input: PrepareInput): Promise<void> {
    const snap = this.getSnapshot();
    if (snap.phase !== 'details' || !this.token || !this.sessionReady) return;
    const name = input.displayName?.trim() ?? null;
    if (input.password.length < PASSWORD_MIN || input.password.length > PASSWORD_MAX) {
      this.set({ fieldError: input.displayName === null
        ? `This form needs a password of at least ${PASSWORD_MIN} characters. If yours is shorter, reset it first, then open the invitation again.`
        : `Choose a password of at least ${PASSWORD_MIN} characters.` });
      return;
    }
    if (input.displayName !== null && (!name || name.length > DISPLAY_NAME_MAX)) {
      this.set({ fieldError: `Enter your name (up to ${DISPLAY_NAME_MAX} characters).` });
      return;
    }
    const { gen, signal } = this.begin();
    this.set({ phase: 'preparing', fieldError: null, notice: null, diagnostic: null });
    const body = { token: this.token, password: input.password, ...(name !== null ? { display_name: name } : {}) };
    try {
      const env = await this.api.request<DataEnvelope<unknown>>('/identity/invitations/prepare', { method: 'POST', body: JSON.stringify(body), signal });
      if (!this.landed(gen)) return;
      const prepared = parsePrepared(env?.data);
      if (!prepared) {
        this.set({ phase: 'details', fieldError: 'The server’s answer wasn’t usable, so nothing was saved. Try again.', diagnostic: { code: 'invalid_response', requestId: null } });
        return;
      }
      this.challenge = prepared.challenge;
      this.existingAccount = input.displayName === null;
      this.set({ phase: 'verify', mfa: prepared.mfa, stepExpiresAt: prepared.expires_at, attempts: 0 });
      this.armStepTimer(prepared.expires_at);
    } catch (error) {
      if (isAbortError(error) || !this.landed(gen)) return;
      this.fail(error, 'prepare');
    }
  }

  /** Step 3: join. A code is required when the server asked for two-step verification. */
  async accept(code: string | null): Promise<void> {
    const snap = this.getSnapshot();
    if (snap.phase !== 'verify' || !this.challenge || !snap.mfa || !this.sessionReady) return;
    if (snap.mfa.required && (code === null || !OTP.test(code))) {
      this.set({ fieldError: 'Enter the 6-digit code from your authenticator app.' });
      return;
    }
    const { gen, signal } = this.begin();
    this.set({ phase: 'accepting', fieldError: null, diagnostic: null });
    const body = { challenge: this.challenge, ...(snap.mfa.required && code ? { mfa_code: code } : {}) };
    try {
      const env = await this.api.request<DataEnvelope<unknown>>('/identity/invitations/accept', { method: 'POST', body: JSON.stringify(body), signal });
      if (!this.landed(gen)) return;
      const accepted = parseAccepted(env?.data);
      const invite = snap.invite;
      const existingAccount = this.existingAccount;
      this.clearSecrets();
      if (!accepted || !invite) {
        // The server may have completed it; never claim success on an unreadable answer.
        this.set({ phase: 'unknown', mfa: null, stepExpiresAt: null, diagnostic: { code: 'invalid_response', requestId: null } });
        return;
      }
      this.set({
        phase: 'joined',
        mfa: null,
        stepExpiresAt: null,
        joined: { tenant: invite.tenant_name, next: accepted.continue_path, fromDesktop: isDesktopContinuation(invite.continue_path), verified: snap.mfa.required, existingAccount },
      });
    } catch (error) {
      if (isAbortError(error) || !this.landed(gen)) return;
      this.fail(error, 'accept');
    }
  }

  private fail(error: unknown, step: 'inspect' | 'prepare' | 'accept'): void {
    const kind = classifyFailure(error);
    const diagnostic = diagnosticOf(error);
    if (kind === 'session') this.onSessionProblem();
    if (kind === 'unavailable') {
      this.clearSecrets();
      this.set({ phase: 'unavailable', mfa: null, diagnostic });
      return;
    }
    if (kind === 'mismatch') {
      this.clearSecrets();
      this.set({ phase: 'wrong_account', mfa: null, diagnostic });
      return;
    }
    if (kind === 'member') {
      this.clearSecrets();
      this.set({ phase: 'already_member', mfa: null, diagnostic });
      return;
    }
    if (step === 'inspect') {
      if (kind === 'invalid') {
        this.clearSecrets();
        this.set({ phase: 'invalid', diagnostic });
      } else this.set({ phase: 'error', diagnostic });
      return;
    }
    if (step === 'prepare') {
      const fieldError =
        kind === 'invalid' ? 'That didn’t work. Check your password: if you already have an account, use its current password. The invitation may also have expired or been revoked.'
          : kind === 'input' ? 'Enter your name and a password of at least 12 characters.'
            : kind === 'limited' ? 'Too many attempts. Wait a while, then try again.'
              : kind === 'session' ? 'Your browser session was refreshed. Enter your password again.'
                : kind === 'unknown' || kind === 'network' ? 'Couldn’t reach the server. Check your connection, then try again.'
                  : 'That didn’t work. Try again.';
      this.set({ phase: 'details', fieldError, diagnostic });
      return;
    }
    // accept
    if (kind === 'unknown') {
      this.clearSecrets();
      this.set({ phase: 'unknown', mfa: null, stepExpiresAt: null, diagnostic });
      return;
    }
    const snap = this.getSnapshot();
    if (kind === 'session') {
      this.dropChallenge();
      this.set({ phase: 'details', mfa: null, notice: 'session_changed', fieldError: null, attempts: 0, diagnostic });
      return;
    }
    if (kind === 'invalid') {
      if (!snap.mfa?.required) {
        this.clearSecrets();
        this.set({ phase: 'invalid', mfa: null, diagnostic });
        return;
      }
      const attempts = snap.attempts + 1;
      if (attempts >= MAX_CODE_ATTEMPTS) {
        this.dropChallenge();
        this.set({ phase: 'details', mfa: null, notice: 'too_many_codes', fieldError: null, attempts: 0, diagnostic });
        return;
      }
      this.set({ phase: 'verify', attempts, fieldError: 'That code didn’t work. Check that your device’s clock is right and enter the current code. The invitation may also have been revoked.', diagnostic });
      return;
    }
    this.set({ phase: 'verify', fieldError: kind === 'limited' ? 'Too many attempts. Wait a while, then try again.' : 'That didn’t work. Try again.', diagnostic });
  }

  private armStepTimer(expiresAt: string): void {
    this.dropStepTimer();
    const ms = Math.max(0, Date.parse(expiresAt) - this.now() - STEP_SLACK_MS);
    this.stepTimer = this.setTimer(() => {
      this.stepTimer = null;
      if (this.getSnapshot().phase !== 'verify') return;
      this.dropChallenge();
      this.set({ phase: 'details', mfa: null, notice: 'step_expired', fieldError: null, attempts: 0 });
    }, Math.min(ms, 2 ** 31 - 1));
  }

  private dropStepTimer(): void {
    if (this.stepTimer !== null) this.clearTimer(this.stepTimer);
    this.stepTimer = null;
  }

  private dropChallenge(): void {
    this.dropStepTimer();
    this.challenge = null;
    this.existingAccount = false;
    if (this.getSnapshot().mfa) this.set({ mfa: null, stepExpiresAt: null });
  }

  private clearSecrets(): void {
    this.dropChallenge();
    this.token = null;
  }
}
